/**
 * ExportButton — "Export" trigger with an Arabic / English xlsx language menu.
 *
 * Opens a small menu offering the two export languages (Arabic default, RTL
 * sheet) and calls `onExport(lang)`. Shows a busy state while the download is in
 * flight and closes on outside click. Direction-aware via getEffectiveDir().
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { ThemedButton } from "./ThemedButton";
import { getEffectiveDir } from "@/shared/lib/direction";
import type { ExportLang } from "@/shared/lib/exportDownload";

interface ExportButtonProps {
  /** Runs the actual download; awaited so the button can show a busy state. */
  onExport: (lang: ExportLang) => Promise<void>;
  /** Trigger label — defaults to the shared `titles:export.button` string. */
  label?: string;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md";
  /** Disables the trigger (e.g. read-only mode); also keeps the menu closed. */
  disabled?: boolean;
  /** Native title attribute — hint text shown on hover when disabled. */
  title?: string;
}

export function ExportButton({ onExport, label, variant = "secondary", size = "md", disabled = false, title }: ExportButtonProps) {
  const { t } = useTranslation("titles");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Horizontal nudge (px) that pulls the menu back inside the viewport when its
  // natural end-anchored position would clip off a screen edge (mobile).
  const [shift, setShift] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the menu when clicking outside it.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keep the menu on-screen: measure its natural rect on open and translate it
  // back inside the viewport if either edge overflows. Direction-agnostic, so it
  // fixes both LTR and RTL regardless of where the trigger sits in its row.
  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let dx = 0;
    if (rect.left < pad) dx = pad - rect.left;
    else if (rect.right > window.innerWidth - pad) dx = window.innerWidth - pad - rect.right;
    setShift(dx);
  }, [open]);

  async function choose(lang: ExportLang) {
    setOpen(false);
    setBusy(true);
    try {
      await onExport(lang);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <ThemedButton
        variant={variant}
        size={size}
        iconStart={<Download size={14} />}
        disabled={busy || disabled}
        title={title}
        onClick={() => setOpen((o) => !o)}
      >
        {busy ? t("export.exporting") : (label ?? t("export.button"))}
      </ThemedButton>

      {open && (
        <div
          ref={menuRef}
          dir={getEffectiveDir()}
          role="menu"
          className="absolute top-full z-50 mt-1.5 min-w-[180px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-[12px] py-1 end-0"
          style={{
            background: "var(--card)",
            border: "1.5px solid var(--color-input-border)",
            boxShadow: "0 8px 28px rgba(26,25,23,0.16)",
            transform: shift ? `translateX(${shift}px)` : undefined,
          }}
        >
          <p
            className="px-3.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: "var(--color-muted-text)" }}
          >
            {t("export.chooseLang")}
          </p>
          {(["ar", "en"] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              role="menuitem"
              onClick={() => void choose(lang)}
              className="block w-full px-3.5 py-2 text-start text-[13.5px] transition-colors hover:bg-[var(--card-hover)]"
              style={{ color: "var(--foreground)" }}
            >
              {t(`export.${lang}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
